import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ReportesService } from '../../services/reportesService';
import { obtenerBorrador, limpiarBorrador, type ReporteDraft } from '../../utils/reporteDraftStorage';
import { formatDateTimeSafe } from '../../utils/dateUtils';

/**
 * Si el operador quedó con un reporte sin confirmar (perdió cobertura justo
 * al enviarlo y la app se cerró o recargó antes de la respuesta del
 * servidor), esto lo detecta al volver a abrir la app y ofrece reintentar
 * sin tener que volver a llenar el formulario.
 */
const ReporteBorradorBanner: React.FC = () => {
  const { t } = useTranslation();
  const [borrador, setBorrador] = useState<ReporteDraft | null>(null);
  const [reintentando, setReintentando] = useState(false);

  useEffect(() => {
    setBorrador(obtenerBorrador());
  }, []);

  if (!borrador) return null;

  const reintentar = async () => {
    setReintentando(true);
    try {
      await ReportesService.crearReporteCompleto(borrador.reporte, borrador.empresaId);
      limpiarBorrador();
      setBorrador(null);
      toast.success(t('reports.draft_recovered_success'));
    } catch (error: any) {
      toast.error(error?.message || t('reports.draft_recovered_error'));
    } finally {
      setReintentando(false);
    }
  };

  const descartar = () => {
    limpiarBorrador();
    setBorrador(null);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-40 bg-amber-500 text-white text-sm px-4 py-2 shadow-lg">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-3 text-center">
        <span>
          ⚠️ {t('reports.draft_pending', { date: formatDateTimeSafe(borrador.guardadoEn) })}
        </span>
        <div className="flex gap-2">
          <button
            onClick={reintentar}
            disabled={reintentando}
            className="px-3 py-1 bg-white bg-opacity-20 rounded text-xs font-medium hover:bg-opacity-30 transition-colors disabled:opacity-50"
          >
            {reintentando ? t('common.loading') : t('reports.draft_retry')}
          </button>
          <button
            onClick={descartar}
            disabled={reintentando}
            className="px-3 py-1 bg-white bg-opacity-20 rounded text-xs font-medium hover:bg-opacity-30 transition-colors disabled:opacity-50"
          >
            {t('reports.draft_discard')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReporteBorradorBanner;
